import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import axios from "../../axiosConfig";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import PreviewAttachmentDialog from "../PreviewAttachmentDialog";
import { useTranslation } from "react-i18next";

const formatDate = (value, isArabic) => {
  if (!value) {
    return "-";
  }

  try {
    return new Date(value).toLocaleString(isArabic ? "ar-SA" : "en-GB");
  } catch {
    return value;
  }
};

const getAttachmentFileTypeIcon = (fileName) => {
  const extension = String(fileName || "")
    .split(".")
    .pop()
    ?.toLowerCase();

  if (["jpg", "jpeg", "png"].includes(extension)) {
    return <ImageOutlinedIcon fontSize="small" sx={{ color: "#2E7D32" }} />;
  }

  if (["pdf"].includes(extension)) {
    return <PictureAsPdfOutlinedIcon fontSize="small" sx={{ color: "#C62828" }} />;
  }

  if (["doc", "docx"].includes(extension)) {
    return <DescriptionOutlinedIcon fontSize="small" sx={{ color: "#1565C0" }} />;
  }

  if (["xls", "xlsx", "csv"].includes(extension)) {
    return <TableChartOutlinedIcon fontSize="small" sx={{ color: "#2E7D32" }} />;
  }

  if (["ppt", "pptx"].includes(extension)) {
    return <SlideshowOutlinedIcon fontSize="small" sx={{ color: "#EF6C00" }} />;
  }

  if (["txt", "rtf"].includes(extension)) {
    return <NotesOutlinedIcon fontSize="small" sx={{ color: "#6D4C41" }} />;
  }

  return <InsertDriveFileIcon fontSize="small" sx={{ color: "text.secondary" }} />;
};

const ReadyTransactionsAttachments = () => {
  const { t, i18n } = useTranslation();
  const isArabic = (i18n.language || "ar").startsWith("ar");

  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [previewAttachment, setPreviewAttachment] = useState(null);

  useEffect(() => {
    const fetchAttachments = async () => {
      try {
        setIsLoading(true);
        setError("");
        const data = await axios.get("/User/ReadyTransactionsAttachments");
        setAttachments(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(t("account_attachmentsLoadError"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAttachments();
  }, [t]);

  const sortedAttachments = useMemo(() => {
    return [...attachments].sort((a, b) => new Date(b.insertedDate || 0) - new Date(a.insertedDate || 0));
  }, [attachments]);

  const groupedAttachments = useMemo(() => {
    const groupsMap = sortedAttachments.reduce((accumulator, attachment) => {
      const groupKey = attachment.attachmentTypeName || t("account_attachmentUnknownType");
      if (!accumulator.has(groupKey)) {
        accumulator.set(groupKey, []);
      }

      accumulator.get(groupKey).push(attachment);
      return accumulator;
    }, new Map());

    return Array.from(groupsMap.entries()).map(([typeName, items]) => ({
      typeName,
      count: items.length,
      items,
    }));
  }, [sortedAttachments, t]);

  const handlePreview = (attachment) => {
    const url = resolveMediaUrl(attachment.attachmentPath);
    setPreviewAttachment({ url, fileName: attachment.attachmentName });
  };

  const handleDownload = (attachment) => {
    const attachmentUrl = resolveMediaUrl(attachment.attachmentPath);
    if (attachmentUrl) {
      window.open(attachmentUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!!error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {groupedAttachments.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">{t("account_attachmentsEmpty")}</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {groupedAttachments.map((group) => (
            <Accordion
              key={group.typeName}
              sx={{
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px !important",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: "rgba(25, 118, 210, 0.04)",
                  "& .MuiAccordionSummary-content": {
                    my: 0,
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  },
                }}
              >
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <FolderOutlinedIcon color="primary" />
                  <Typography sx={{ fontWeight: 700 }}>
                    {group.typeName}
                  </Typography>
                </Stack>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${group.count} ${t("account_attachmentCount")}`}
                />
              </AccordionSummary>

              <AccordionDetails sx={{ p: 0 }}>
                <Stack divider={<Divider flexItem />}>
                {group.items.map((attachment) => (
                  <Box
                    key={attachment.attachmentId}
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                      <InsertDriveFileIcon color="primary" sx={{ mt: 0.2 }} />
                      <Stack sx={{ minWidth: 0, flex: 1 }} spacing={0.35}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexWrap: "wrap" }}>
                          <Typography sx={{ fontWeight: 700, minWidth: 0 }} noWrap title={attachment.attachmentName}>
                            {attachment.attachmentName}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            icon={getAttachmentFileTypeIcon(attachment.attachmentName)}
                            label=""
                            sx={{
                              height: 22,
                              width: 34,
                              "& .MuiChip-label": { display: "none" },
                              "& .MuiChip-icon": { marginLeft: 0, marginRight: 0 },
                            }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {t("account_attachmentRequestName")}: {attachment.subscriberServiceName || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {t("account_attachmentRequestNumber")}: {attachment.subscriberServiceFullNumber || attachment.subscriberServiceId || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("account_attachmentsUploadedAt")}: {formatDate(attachment.insertedDate, isArabic)}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Stack direction="row" spacing={0.5}>
                      <IconButton color="primary" onClick={() => handlePreview(attachment)} title={t("account_preview")}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton color="primary" onClick={() => handleDownload(attachment)} title={t("account_download")}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      )}

      {previewAttachment && (
        <PreviewAttachmentDialog
          open={Boolean(previewAttachment)}
          handleClose={() => setPreviewAttachment(null)}
          attachmentDetails={previewAttachment}
        />
      )}
    </Box>
  );
};

export default ReadyTransactionsAttachments;
